import { useState } from "react";
import { OctagonX } from "lucide-react";
import { radarLabApi } from "services/radarLabApi";

// A button that asks first: the question and what follows appear inline (the
// field pages' confirm), and only the second click acts.
export function ConfirmAction({
  label,
  question,
  confirmLabel,
  onConfirm,
  className = "field-button",
  confirmClassName = "field-button primary",
  disabled = false,
  icon: Icon = null,
  children,
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const act = async () => {
    setBusy(true);
    setError("");
    try {
      const message = await onConfirm();
      setDone(typeof message === "string" ? message : "");
      setAsking(false);
    } catch (e) {
      setError(e.message || "The request failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="field-lab-confirm">
      {!asking && (
        <button
          type="button"
          className={className}
          disabled={disabled || busy}
          onClick={() => {
            setDone("");
            setError("");
            setAsking(true);
          }}
        >
          {Icon && <Icon size={15} />}
          {label}
        </button>
      )}
      {asking && (
        <div className="field-radar-confirm" role="group" aria-label={question}>
          <p>
            <b>{question}</b> {children}
          </p>
          <div className="field-actions" style={{ marginTop: 10 }}>
            <button
              type="button"
              className={confirmClassName}
              disabled={busy}
              onClick={act}
            >
              {busy ? "Sending…" : confirmLabel}
            </button>
            <button
              type="button"
              className="field-button"
              disabled={busy}
              onClick={() => setAsking(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && (
        <div className="field-alert" role="alert" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}
      {done && (
        <p className="field-caption" role="status">
          {done}
        </p>
      )}
    </div>
  );
}

// The greenhouse controller's own pump stop (POST .../pump-stop/ on the main
// backend, the same as Stop on the Experiment page), for when water must stop
// moving now. The lab run itself cannot stop a running pump: its steps run on
// the greenhouse backend.
export function EmergencyPumpStop({ controller, onDone }) {
  const slug = controller?.slug || controller?.mac || "";
  const name = controller?.name || slug || "the controller";
  return (
    <div className="field-lab-emergency">
      <ConfirmAction
        label="Emergency pump stop"
        icon={OctagonX}
        className="field-button emergency"
        confirmClassName="field-button emergency"
        question={`Stop the pump on ${name} now?`}
        confirmLabel="Confirm: stop the pump"
        disabled={!slug}
        onConfirm={async () => {
          await radarLabApi.emergencyPumpStop(slug);
          onDone?.();
          return "Pump stop sent to the controller. Take a ruler reading before any further step.";
        }}
      >
        This sends the controller's pump stop, the same as Stop on the
        Experiment page. A step still waiting for its photo is cancelled; a
        step that is pumping stops early, so the tank level is no longer known
        from the plan.
      </ConfirmAction>
      <small>
        Use it if water is going where it should not. It does not end the lab
        run by itself: the lab run stops when it sees the step cut short.
      </small>
    </div>
  );
}
